import { useGSAP } from '@gsap/react'
import { graphql } from '@typings/gql'
import { isBrowser } from '@utils/is-browser'
import gsap from 'gsap'
import React, { useEffect, useRef, useState } from 'react'
import { useQuery } from 'urql'

import { type TabId, TABS } from './data'
import SearchInput from './input'
import ResultsMapper from './results-mapper'
import Suggestions from './suggestions'
import TabsBar from './tabs-bar'
import { useSearchTrackingSession } from './tracking-session-provider'
import UsefulLinks from './useful-links'

const SEARCH_QUERY = graphql(`
  query FetchSplitSearchResults(
    $query: String!
    $searchSessionToken: String!
    $includeMarketResponse: Boolean!
  ) {
    inbentaResponse: getFederatedSearches(
      context: { scope: PROSPECT }
      sessionToken: $searchSessionToken
      query: $query
      options: [
        { category: PRODUCT, limit: 3, type: search }
        { category: BLOG, limit: 3, type: search }
        { category: FAQ, limit: 5, type: search }
      ]
      source: "web-search"
    ) @skip(if: $includeMarketResponse) {
      __typename
      result {
        ... on InbentaSearchResult {
          __typename
          items {
            ...SearchResultsProductItem
            ...SearchResultsBlogItem
            ...SearchResultsFaqItem
          }
          category
          total
          tracking {
            searchCode
          }
        }
      }
      error {
        ...SearchErrorField
      }
    }
    marketResponse: getMarketSearch(
      context: { scope: PROSPECT }
      query: $query
      options: { limit: 5 }
    ) @include(if: $includeMarketResponse) {
      __typename
      result {
        __typename
        items {
          ...SearchResultsMarketItem
        }
        total
      }
      error {
        ...SearchErrorField
      }
    }
  }
`)

type Props = {
  isLightMode?: boolean
}

const Search = ({ isLightMode }: Props) => {
  const containerRef = useRef<HTMLDivElement>()
  const timeout = useRef(null)

  const [inputValue, setInputValue] = useState('')
  const [term, setTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<TabId>(TABS[0].id)

  const session = useSearchTrackingSession()

  const [{ fetching, data, error }] = useQuery({
    query: SEARCH_QUERY.toString(),
    variables: {
      query: term,
      searchSessionToken: session?.search?.sessionToken,
      includeMarketResponse: selectedCategory === 'MARKET',
    },
    pause: !isBrowser || !session,
  })

  const DISPLAY_SUGGESTIONS = !data || !inputValue || !term

  useEffect(() => {
    setIsLoading(true)
    timeout.current = setTimeout(() => {
      setTerm(inputValue)
      timeout.current = null
      setIsLoading(false)
    }, 800)
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [inputValue])

  useGSAP(() => {
    gsap.set(containerRef.current, {
      y: 30,
      opacity: 0,
    })

    gsap.to(containerRef.current, {
      y: 0,
      opacity: 1,
      duration: 0.3,
      ease: 'power2.inOut',
    })
  }, [])

  useGSAP(() => {
    gsap.fromTo(
      containerRef.current,
      {
        height: containerRef.current.scrollHeight,
      },
      {
        height: 'auto',
        duration: 0.3,
        ease: 'power2.inOut',
        onComplete: () => {
          gsap.set(containerRef.current, {
            height: containerRef.current.scrollHeight,
          })
        },
      }
    )
  }, [selectedCategory, data, inputValue])

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full flex-col gap-8 pb-4 pt-8 tablet:gap-12 tablet:pb-12 tablet:pt-12"
    >
      <SearchInput
        inputValue={inputValue}
        isLightMode={isLightMode}
        isLoading={fetching}
        setInputValue={setInputValue}
      />
      <div className="flex h-full flex-col gap-8">
        {!DISPLAY_SUGGESTIONS && (
          <TabsBar
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
          />
        )}
        <div className="flex flex-col gap-8 pr-8">
          {DISPLAY_SUGGESTIONS ? (
            <div className="flex flex-col gap-16 tablet:gap-20 laptop:flex-row">
              <Suggestions />
              <UsefulLinks />
            </div>
          ) : (
            <ResultsMapper
              category={selectedCategory}
              data={data}
              error={Boolean(error)}
              fetching={fetching || isLoading}
              inputValue={inputValue}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default Search
